import { metrics } from '@/src/homepage/common';

/**
 * Direct client for geo-service-http (the ECS/Fargate HTTP rewrite of PySOA geo_service).
 * Formerly gated behind the `enable_geo_service_http_ecs_canary` Split flag as a canary
 * alternative to routing through the geoproxyservice PySOA proxy Lambda; the flag reached
 * 100% rollout and was removed, so this is now the only code path.
 *
 * The URL/param mapping here mirrors geoproxyservice's own ECS canary implementation
 * (pysoa-proxy-geo-service/geo_lambda/pysoa_proxy_geo_service/ecs_canary.py) so that this
 * client exercises the exact same, already-verified request shapes. geo-service-http wraps
 * every response as `{"data": <pysoa-action-response-body>, "request_id": ...}` (see
 * geo_service/geo_service/http/views.py::_run_action), so unwrapping `data` yields a body
 * that is byte-for-byte identical to what the PySOA proxy client returns today.
 */

const DEFAULT_TIMEOUT_MS = 2000;

// Deliberately no default/fallback URL here: GEO_ECS_ALB_URL is set per-stage by the CDK
// (see GEO_ECS_ALB_URL_MAP in cdk/lib/constants.ts). Falling back to a hardcoded URL (e.g.
// prod's) would risk QA/dev traffic silently hitting the wrong environment if the env var
// were ever missing or misconfigured, so we fail loudly instead — callers already treat any
// error from this client as a signal to fall back to the PySOA-proxy path.
function getAlbUrl(): string {
    const albUrl = process.env.GEO_ECS_ALB_URL?.trim();
    if (!albUrl) {
        throw new Error(
            'GEO_ECS_ALB_URL is not configured for this environment; refusing to fall back to a default ALB URL',
        );
    }
    return albUrl.replace(/\/$/, '');
}

function getTimeoutMs(): number {
    const raw = process.env.GEO_ECS_TIMEOUT_MS;
    const parsed = raw ? Number(raw) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_TIMEOUT_MS;
}

function toQueryString(params: Record<string, string | number | boolean | undefined>): string {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            search.set(key, String(value));
        }
    });
    return search.toString();
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function fetchJson(path: string): Promise<any> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), getTimeoutMs());

    try {
        // Computed inside the try so a throw from getAlbUrl() (missing/blank
        // GEO_ECS_ALB_URL) still hits the finally below and clears the abort timer,
        // instead of leaking a scheduled setTimeout until it fires on its own.
        const url = `${getAlbUrl()}${path}`;
        let response: Response;
        try {
            response = await fetch(url, {
                method: 'GET',
                signal: controller.signal,
            });
        } catch (cause) {
            // fetch() itself rejected -- DNS failure (ENOTFOUND), connection refused, TLS
            // error, or our own AbortController firing on timeout. Node's fetch wraps the
            // real reason in a TypeError with a `.cause`, or throws a DOMException named
            // "AbortError" on timeout; surface that detail on the rethrown error (message +
            // `cause` property) rather than losing it, since callers otherwise have no way to
            // tell "DNS lookup failed" apart from "timed out" apart from "connection refused".
            const isAbort = cause instanceof Error && cause.name === 'AbortError';
            const reason = isAbort
                ? `timed out after ${getTimeoutMs()}ms`
                : cause instanceof Error
                  ? String(cause.cause ?? cause.message)
                  : String(cause);
            throw Object.assign(new Error(`geo-service-http request to ${url} failed: ${reason}`), {
                url,
                cause,
            });
        }

        if (!response.ok) {
            // Best-effort read of the response body so the caller can log *why* geo-service-http
            // rejected the request (validation error, 5xx stack trace snippet, etc.) instead of
            // just the bare status code. Guarded because some callers (tests) stub a response
            // object without a `.text` method.
            let bodyText: string | undefined;
            if (typeof response.text === 'function') {
                try {
                    bodyText = await response.text();
                } catch {
                    // best effort only -- body is optional context, not worth failing over.
                }
            }
            throw Object.assign(
                new Error(
                    `geo-service-http request failed with status ${response.status} for ${url}` +
                        (bodyText ? `: ${bodyText}` : ''),
                ),
                { status: response.status, url, body: bodyText },
            );
        }

        const json = await response.json();

        // geo-service-http always wraps a successful response as
        // `{"data": <pysoa-action-response-body>, "request_id": ...}` (see the module
        // docstring above). We check for the `data` key with `in` rather than unwrapping
        // via `json?.data ?? json`, because `??` cannot distinguish "data is present but
        // explicitly null" from "data key is missing entirely" -- both fall through to the
        // same branch, and either way that previously returned the raw outer envelope
        // (`{ data: null, request_id }` or the whole `json`) instead of a PySOA-shaped body.
        //
        // - `data` present but null: geoproxyservice's own ECS canary
        //   (pysoa-proxy-geo-service/geo_lambda/pysoa_proxy_geo_service/ecs_canary.py::
        //   _unwrap_ecs_response) unwraps via `resp_json.get("data", resp_json)`, which
        //   correctly returns the (null) value rather than the envelope in this case. We go
        //   one step further and normalize null to `{}` so canary callers get an empty
        //   object shaped like a normal (if empty) PySOA action response, matching what
        //   downstream code that destructures fields off the body expects, instead of a
        //   bare `null`.
        // - `data` key missing entirely: this violates the documented response contract, so
        //   rather than silently exposing the envelope (or guessing at a default), we throw.
        //   Callers already treat any error from this client as a signal to fall back to the
        //   PySOA-proxy path, which is the safe behavior for a malformed/unexpected response.
        if (json && typeof json === 'object' && 'data' in json) {
            return json.data ?? {};
        }

        throw new Error('geo-service-http response is missing the expected "data" envelope');
    } finally {
        clearTimeout(timeoutId);
    }
}

// PySOA's "not found" result is a bare `null`. `fetchJson` above normalizes that to `{}` so
// that callers destructuring a nested key off the body (e.g. `response?.['place']`) get
// `undefined` -- and thus `null` after their own `|| null` -- rather than throwing on a bare
// `null`. But callers that return the (unwrapped) body directly instead of destructuring a
// nested key must check for this empty-object case themselves, since `{}` is truthy and a
// plain `response || null` would otherwise surface "not found" as `{}` instead of `null`.
export function isEmptyEcsResponse(response: unknown): boolean {
    return typeof response === 'object' && response !== null && Object.keys(response).length === 0;
}

export async function ecsGetPlaceFromCoordinates(params: {
    latitude: number;
    longitude: number;
    placeType?: string;
    locale?: string;
}) {
    const qs = toQueryString({
        lat: params.latitude,
        lng: params.longitude,
        place_type: params.placeType || 'locality',
        locale: params.locale,
    });

    return metrics.measure(
        () => fetchJson(`/v1/geo/places/by-coordinates?${qs}`),
        'ecsGetPlaceFromCoordinates',
        {
            ServiceName: 'geo-service-http-ecs',
        },
    );
}

export async function ecsGetLocationFromCoordinates(params: {
    latitude: number;
    longitude: number;
}) {
    const qs = toQueryString({
        lat: params.latitude,
        lng: params.longitude,
    });

    return metrics.measure(
        () => fetchJson(`/v1/geo/locations/by-coordinates?${qs}`),
        'ecsGetLocationFromCoordinates',
        {
            ServiceName: 'geo-service-http-ecs',
        },
    );
}

export async function ecsGetLocationFromIP(ipAddress: string) {
    return metrics.measure(
        () => fetchJson(`/v1/geo/locations/by-ip/${encodeURIComponent(ipAddress)}`),
        'ecsGetLocationFromIP',
        {
            ServiceName: 'geo-service-http-ecs',
        },
    );
}

export async function ecsGetPlaceFromLocationSlug(params: {
    locationSlug: string;
    locale?: string;
}) {
    const qs = toQueryString({
        slug: params.locationSlug,
        locale: params.locale,
    });

    return metrics.measure(
        () => fetchJson(`/v1/geo/places/by-slug?${qs}`),
        'ecsGetPlaceFromLocationSlug',
        {
            ServiceName: 'geo-service-http-ecs',
        },
    );
}

export async function ecsGetPlaceFromGooglePlaceId(params: {
    googlePlaceId: string;
    excludeNeighborhoods?: boolean;
    enableNeighborhoodsAndBoroughs?: boolean;
    locale?: string;
}) {
    const qs = toQueryString({
        id: params.googlePlaceId,
        exclude_neighborhoods: params.excludeNeighborhoods,
        enable_neighborhoods_and_boroughs: params.enableNeighborhoodsAndBoroughs,
        locale: params.locale,
    });

    return metrics.measure(
        () => fetchJson(`/v1/geo/places/by-google-place-id?${qs}`),
        'ecsGetPlaceFromGooglePlaceId',
        {
            ServiceName: 'geo-service-http-ecs',
        },
    );
}
